import {
  CARD_DESCRIPTION_FIELD_HINT,
  CARD_DESCRIPTION_LIMIT_MESSAGE,
  CARD_DESCRIPTION_MAX
} from "../chunk-6RHVH33O.js";

// src/tool-contracts/spec.ts
var f = {
  string(opts) {
    return { kind: "string", ...opts };
  },
  number(opts) {
    return { kind: "number", ...opts };
  },
  boolean(opts) {
    return { kind: "boolean", ...opts };
  },
  enum(values, opts) {
    return { kind: "enum", values, ...opts };
  },
  array(item, opts) {
    return { kind: "array", item, ...opts };
  },
  object(fields, opts) {
    return { kind: "object", fields, ...opts };
  },
  optional(inner) {
    return { kind: "optional", inner };
  },
  nullable(inner) {
    return { kind: "nullable", inner };
  }
};

// src/tool-contracts/compile.ts
function compileString(z, spec) {
  let schema = z.string();
  if (spec.min !== void 0) schema = schema.min(spec.min);
  if (spec.max !== void 0) schema = schema.max(spec.max);
  return schema;
}
function compileNumber(z, spec) {
  let schema = z.number();
  if (spec.int) schema = schema.int();
  if (spec.positive) schema = schema.positive();
  if (spec.nonnegative) schema = schema.nonnegative();
  if (spec.min !== void 0) schema = schema.min(spec.min);
  if (spec.max !== void 0) schema = schema.max(spec.max);
  return schema;
}
function compileArray(z, spec) {
  let schema = z.array(compileField(z, spec.item));
  if (spec.min !== void 0) schema = schema.min(spec.min);
  return schema;
}
function compileBase(z, spec) {
  switch (spec.kind) {
    case "string":
      return compileString(z, spec);
    case "number":
      return compileNumber(z, spec);
    case "boolean":
      return z.boolean();
    case "enum":
      return z.enum([...spec.values]);
    case "array":
      return compileArray(z, spec);
    case "object":
      return z.object(compileShape(z, spec.fields));
  }
}
function compileField(z, spec) {
  if (spec.kind === "optional") {
    return compileField(z, spec.inner).optional();
  }
  if (spec.kind === "nullable") {
    return compileField(z, spec.inner).nullable();
  }
  const schema = compileBase(z, spec);
  return spec.desc === void 0 ? schema : schema.describe(spec.desc);
}
function compileShape(z, fields) {
  const shape = {};
  for (const [key, spec] of Object.entries(fields)) {
    shape[key] = compileField(z, spec);
  }
  return shape;
}

// src/tool-contracts/contract.ts
function defineToolContract(contract) {
  return contract;
}

// src/tool-contracts/fields.ts
var mcpProjectId = f.optional(f.string({ desc: "Target Conveyor project ID" }));
var cardDescriptionDesc = (lead) => `${lead} \u2014 ${CARD_DESCRIPTION_FIELD_HINT}`;
var storyPointValueDesc = "Story point value (1=Common, 2=Magic, 3=Rare, 5=Unique)";

// src/tool-contracts/tasks.ts
var getTaskContract = defineToolContract({
  name: "get_task",
  agent: {
    description: "Look up any task by slug or ID. Returns JSON with id, slug, title, description, plan, status, branch, githubPRNumber, githubPRUrl, storyPoints. For children use list_subtasks.",
    fields: {
      slug_or_id: f.string({ desc: "The task slug (e.g. 'my-task') or CUID" })
    }
  },
  mcp: {
    description: "Get full task details including plan, chat history, PR info, subtasks, and build status. Pass projectId to target a specific project; otherwise the configured default project is used.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({
        desc: "The task ID or slug (the value in a card URL, /cards/<slug>)"
      })
    }
  }
});
var postToChatContract = defineToolContract({
  name: "post_to_chat",
  agent: {
    description: "Post a message to the task chat for the team to see. Your turn output is NOT shown in chat, so this is the only way the team sees your status, summaries, and questions. Omit task_id to post to the current task's chat; pass a child's ID to message its chat.",
    fields: {
      message: f.optional(f.string({ desc: "The message to post to the team" })),
      content: f.optional(
        f.string({
          desc: "Alias of `message` (the external conveyor-mcp surface names this field `content`). Provide exactly one of the two."
        })
      ),
      task_id: f.optional(
        f.string({
          desc: "Child task ID to post to. Omit to post to the current task's chat."
        })
      ),
      milestone: f.optional(
        f.enum(["plan_ready", "implementation_complete", "blocked"], {
          desc: "Declare a narrative milestone instead of a routine update. Use SPARINGLY \u2014 only when the plan is ready, implementation is complete, or you are blocked. Milestones appear on the card's activity timeline and in Slack."
        })
      )
    }
  },
  mcp: {
    description: "Post a message to a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The task ID" }),
      content: f.optional(f.string({ desc: "Message content" })),
      message: f.optional(
        f.string({
          desc: "Alias of `content` (the in-pod agent surface names this field `message`). Provide exactly one of the two."
        })
      )
    }
  }
});
var readTaskChatContract = defineToolContract({
  name: "read_task_chat",
  agent: {
    description: "Read recent human/user chat messages for a task. Omit task_id for the current task; pass a child ID for a child's chat. For agent logs use get_execution_logs.",
    fields: {
      limit: f.optional(f.number({ desc: "Number of recent messages to fetch (default 20)" })),
      task_id: f.optional(
        f.string({
          desc: "Child task ID to read chat from. Omit to read the current task's chat."
        })
      )
    }
  },
  mcp: {
    description: "Read messages from a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used. For agent execution logs use get_task_logs.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The task ID" }),
      limit: f.optional(f.number({ desc: "Max messages to return (default 50)" }))
    }
  }
});
var listTagsContract = defineToolContract({
  name: "list_tags",
  agent: {
    description: "List the project glossary: every tag's id, name, color, description, parent/child tag ids, its contextPaths (the rule/doc/file/folder links it wires into agent context \u2014 `[]` means none), and whether it carries a full overview (fetch that with get_tag). The context links ship inline, so you only need get_tag for a term's full overview. Use the ids with get_tag / update_tag.",
    fields: {}
  },
  mcp: {
    description: "List all project tags with names, IDs, colors, descriptions, hierarchy (parent/child ids), contextPaths (the rule/doc/file/folder links each tag wires into agent context \u2014 `[]` means none), and a hasOverview flag. Context links ship inline; call get_tag only for a term's full overview. Pass projectId to target a specific project; otherwise the configured default project is used.",
    fields: {
      projectId: mcpProjectId
    }
  }
});
var SEARCH_STATUSES = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];
var SEARCH_CARD_TYPES = ["task", "incident", "suggestion"];
var searchFilterFields = {
  tagNames: f.optional(
    f.array(f.string(), {
      desc: 'Tag names to filter by, e.g. ["agent", "mcp"]. Names, not ids, and matched case-insensitively. Use list_tags to see the glossary.'
    })
  ),
  tagMatch: f.optional(
    f.enum(["any", "all"], {
      desc: 'How to combine tagNames: "any" (default) returns cards carrying at least one, "all" returns only cards carrying every one.'
    })
  ),
  includeChildTags: f.optional(
    f.boolean({
      desc: "Also match cards carrying any descendant of the named tags in the tag hierarchy. Use this to sweep a whole area from its parent tag, e.g. cloud-build also matching release and deployment. Default false."
    })
  ),
  searchQuery: f.optional(f.string({ desc: "Text search on title and description" })),
  statusFilters: f.optional(
    f.array(f.enum(SEARCH_STATUSES), { desc: "Filter by one or more statuses" })
  ),
  typeFilters: f.optional(
    f.array(f.enum(SEARCH_CARD_TYPES), {
      desc: 'Card types to include (default ["task"]). Pass e.g. ["incident"] or list several to search across types.'
    })
  ),
  assigneeId: f.optional(f.string({ desc: "Filter by assigned user ID" })),
  unassigned: f.optional(
    f.boolean({
      desc: "Only return cards with no assignee (mutually exclusive with assigneeId)"
    })
  ),
  limit: f.optional(f.number({ desc: "Max results to return (default 20)" }))
};
var searchResultNote = "Every result lists the tags it carries, so you can see why it matched. Results are relevance-ordered: highest priority first then newest; suggestions-only queries rank by upvote score. Returns summaries \u2014 plan omitted, description truncated; use get_task for full details.";
var searchTasksContract = defineToolContract({
  name: "search_tasks",
  agent: {
    description: `Search this project's cards by tag, text, status, type, and/or assignment. Tags are the project's glossary, so tagNames is the fastest way to find prior work in the area you are touching \u2014 e.g. every ReviewPR card tagged "mcp". Searches the whole project, not just the current card. Defaults to type=task \u2014 pass typeFilters for incidents/suggestions. ${searchResultNote}`,
    fields: searchFilterFields
  },
  mcp: {
    description: `Search cards by tag name, text query, status, type, and/or assignment. Defaults to type=task \u2014 pass typeFilters to include incidents/suggestions. Pass projectId to target a specific project; otherwise the configured default project is used. Use tag names like 'agent-runner', not IDs. ${searchResultNote}`,
    fields: {
      projectId: mcpProjectId,
      ...searchFilterFields,
      subProjectId: f.optional(
        f.nullable(
          f.string({
            desc: "Filter to a sub-project board. Omit to use the connection's default board (CONVEYOR_SUBPROJECT_ID) when set, else the whole project; pass null to force the whole project. Use list_accessible_subprojects to find board IDs."
          })
        )
      )
    }
  }
});
var childTaskIdForMerge = f.string({
  desc: "The child task ID whose PR should be approved and merged"
});
var approveAndMergePrContract = defineToolContract({
  name: "approve_and_merge_pr",
  agent: {
    description: "Approve and merge a child task's PR. Preconditions: child in ReviewPR. Returns { merged }: true = merged (status\u2192ReviewDev); false = automerge queued, wait for ReviewDev. Requires project Admin, or a sub-project merge grant covering every changed file; release PRs require Admin.",
    fields: {
      childTaskId: childTaskIdForMerge
    }
  },
  mcp: {
    description: "Approve a child task's pull request and QUEUE it for merge \u2014 the merge lands asynchronously (~30s sweep) once the CI and code-review gates pass; the response says whether it merged or was queued, so verify PR state before depending on it. Pass projectId to target a specific project; otherwise the configured default project is used. The child task must be in ReviewPR status with a PR. Requires project Admin, or a sub-project merge grant covering every changed file; release PRs require Admin.",
    fields: {
      projectId: mcpProjectId,
      childTaskId: childTaskIdForMerge
    }
  }
});
var tasksContracts = [
  getTaskContract,
  postToChatContract,
  readTaskChatContract,
  listTagsContract,
  searchTasksContract,
  approveAndMergePrContract
];

// src/tool-contracts/tags.ts
var tagRef = f.string({
  desc: "Tag id, or the exact tag name (case-insensitive)",
  min: 1,
  max: 100
});
var getTagContract = defineToolContract({
  name: "get_tag",
  agent: {
    description: "Read one tag's full glossary entry: description, the full markdown overview (the term's spec \u2014 philosophy, mechanics, invariants), linked files/rules (each with its verified-link status \u2014 ok/stale/unchecked \u2014 from the periodic repo check), parent/child tags, active-card count, and recent revisions with their reasons. Call this whenever a chat message, plan, or tag list points at a term you need the full context for. A response with `overviewPath` set means the overview is sourced from that repo file \u2014 prefer Reading the path from your checkout (branch-correct); the served overview is the base-branch materialization (`overviewSource.state`: ok/pending/stale).",
    fields: {
      tag: tagRef
    }
  },
  mcp: {
    description: "Read one tag's full glossary entry \u2014 description, markdown overview, context links (each with its verified-link status: ok/stale/unchecked plus last-checked provenance), parent/child tags, active-card count, and recent revisions with provenance. A response with `overviewPath` set means the overview is sourced from that repo file at the base branch (`overviewSource.state`: ok/pending/stale) \u2014 clients with a checkout can read the path directly for the branch-correct copy. Pass projectId to target a specific project; otherwise the configured default project is used.",
    fields: {
      projectId: mcpProjectId,
      tag: tagRef
    }
  }
});
var tagsContracts = [getTagContract];

// src/tool-contracts/checklists.ts
var mcpChecklistTaskId = f.string({ desc: "The task ID or slug" });
var testStatuses = f.optional(
  f.array(f.enum(["open", "approved", "rejected"]), {
    desc: "Filter tests by status: open | approved | rejected"
  })
);
var setManualTestItems = f.array(
  f.object({ title: f.string({ min: 1, desc: "A concise, actionable test step" }) }),
  { min: 1, desc: "List of manual test steps to add" }
);
var titleToEdit = f.string({ min: 1, desc: "The current title of the manual test to edit" });
var newTitle = f.string({ min: 1, desc: "The new title for the manual test" });
var titleToRemove = f.string({ min: 1, desc: "The title of the manual test to remove" });
var titleToApprove = f.string({ min: 1, desc: "The title of the manual test to approve" });
var titleToReject = f.string({ min: 1, desc: "The title of the manual test to reject" });
var rejectReason = f.string({
  min: 1,
  max: 2e3,
  desc: "Why the test failed \u2014 what went wrong, shown to the team"
});
var listManualTestsContract = defineToolContract({
  name: "list_manual_tests",
  agent: {
    description: "List the manual test checklist items for the current task. Use to see what manual verification steps have already been recorded.",
    fields: {}
  },
  mcp: {
    description: "List the manual test checklist items for a task. Pass projectId to target a specific project; otherwise the configured default project is used. Use to see what manual verification steps have already been recorded.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The task ID or slug (the value in a card URL, /cards/<slug>)" })
    }
  }
});
var queryManualTestsContract = defineToolContract({
  name: "query_manual_tests",
  agent: {
    description: "Query manual tests across many tasks in this project, grouped by task. Filter by card status (ReviewDev, ReviewLive, Complete, ...) and/or test status (open | approved | rejected). Use to answer 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards.",
    fields: {
      cardStatuses: f.optional(
        f.array(f.string(), {
          desc: 'Filter tasks by card status, e.g. ["ReviewDev", "ReviewLive"]'
        })
      ),
      testStatuses
    }
  },
  mcp: {
    description: "Query manual tests across many tasks in a project, grouped by task. Filter by card status (e.g. ReviewDev, ReviewLive, Complete) and/or test status (open | approved | rejected). Use to answer questions like 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests in ReviewDev/ReviewLive with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards. Pass projectId to target a specific project; otherwise the configured default project is used.",
    fields: {
      projectId: mcpProjectId,
      cardStatuses: f.optional(
        f.array(f.string(), {
          desc: 'Filter tasks by card/column status, e.g. ["ReviewDev", "ReviewLive"]'
        })
      ),
      testStatuses
    }
  }
});
var setManualTestsContract = defineToolContract({
  name: "set_manual_tests",
  agent: {
    description: "Add manual test steps to the task checklist. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing this PR.",
    fields: {
      items: setManualTestItems
    }
  },
  mcp: {
    description: "Add manual test steps to a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing the task's PR.",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpChecklistTaskId,
      items: setManualTestItems
    }
  }
});
var editManualTestContract = defineToolContract({
  name: "edit_manual_test",
  agent: {
    description: "Rename an existing manual test step. Identify the test by its current title (case-insensitive); pass the new title to replace it. Use to correct or refine a recorded manual verification step.",
    fields: {
      title: titleToEdit,
      newTitle
    }
  },
  mcp: {
    description: "Rename an existing manual test step on a task. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its current title (case-insensitive) and pass the new title to replace it.",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpChecklistTaskId,
      title: titleToEdit,
      newTitle
    }
  }
});
var removeManualTestContract = defineToolContract({
  name: "remove_manual_test",
  agent: {
    description: "Remove an existing manual test step from the task checklist. Identify the test by its title (case-insensitive). Use to delete a stale or incorrect manual verification step.",
    fields: {
      title: titleToRemove
    }
  },
  mcp: {
    description: "Remove an existing manual test step from a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive).",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpChecklistTaskId,
      title: titleToRemove
    }
  }
});
var approveManualTestContract = defineToolContract({
  name: "approve_manual_test",
  agent: {
    description: "Sign off on (approve) a manual test step on behalf of your authenticated user. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    fields: {
      title: titleToApprove
    }
  },
  mcp: {
    description: "Sign off on (approve) a manual test step on a task on behalf of your authenticated user. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpChecklistTaskId,
      title: titleToApprove
    }
  }
});
var rejectManualTestContract = defineToolContract({
  name: "reject_manual_test",
  agent: {
    description: "Flag an issue with (reject) a manual test step on behalf of your authenticated user, recording the reason. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    fields: {
      title: titleToReject,
      reason: rejectReason
    }
  },
  mcp: {
    description: "Flag an issue with (reject) a manual test step on a task on behalf of your authenticated user, recording the reason. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpChecklistTaskId,
      title: titleToReject,
      reason: rejectReason
    }
  }
});
var checklistContracts = [
  listManualTestsContract,
  queryManualTestsContract,
  setManualTestsContract,
  editManualTestContract,
  removeManualTestContract,
  approveManualTestContract,
  rejectManualTestContract
];

// src/tool-contracts/dependencies.ts
var getDependenciesContract = defineToolContract({
  name: "get_dependencies",
  agent: {
    description: "Get this task's dependencies and their met/unmet status (met = merged to dev). Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    fields: {}
  },
  mcp: {
    description: "Get a task's dependencies and their met/unmet status (met = merged to dev). Pass projectId to target a specific project; otherwise the configured default project is used. Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The task ID" })
    }
  }
});
var addDependencyContract = defineToolContract({
  name: "add_dependency",
  agent: {
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. For post-task follow-ups use create_follow_up_task instead.",
    fields: {
      depends_on_slug_or_id: f.string({ desc: "Slug or ID of the task this task depends on" })
    }
  },
  mcp: {
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. Pass projectId to target a specific project; otherwise the configured default project is used.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The task ID that will be blocked" }),
      dependsOnSlugOrId: f.string({ desc: "Slug or ID of the task this one depends on" })
    }
  }
});
var removeDependencyContract = defineToolContract({
  name: "remove_dependency",
  agent: {
    description: "Remove a previously added dependency from this task. When to use: the dependency was added in error or is no longer relevant. Returns: confirmation string.",
    fields: {
      depends_on_slug_or_id: f.string({ desc: "Slug or ID of the task to remove as dependency" })
    }
  },
  mcp: {
    description: "Remove a previously added dependency from a task. Pass projectId to target a specific project; otherwise the configured default project is used. The task is no longer blocked by the named task. Returns: confirmation string.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The task ID to unblock" }),
      dependsOnSlugOrId: f.string({ desc: "Slug or ID of the dependency to remove" })
    }
  }
});
var dependenciesContracts = [
  getDependenciesContract,
  addDependencyContract,
  removeDependencyContract
];

// src/tool-contracts/subtasks.ts
var SP_DESCRIPTION = storyPointValueDesc;
var AGENT_FOLLOW_PARENT_STATUS = "Child mirrors the parent task's status automatically \u2014 for subtasks that ship on the parent's branch/PR with no build or PR of their own. Manual status writes on a follower stick only until the parent's next transition.";
var MCP_FOLLOW_PARENT_STATUS = "When true, this subtask mirrors the parent task's status automatically \u2014 for children that ship on the parent's branch/PR and have no build or PR of their own. Manual status writes on a follower stick only until the parent's next transition.";
var AGENT_DEPENDS_ON = "Sibling subtask ids or slugs this subtask blocks on (it won't start until they merge to dev). Set explicit dependency metadata here instead of describing order in the plan text \u2014 the pack runner schedules children off these edges. Omit / leave empty for independent children so they run in parallel.";
var MCP_STATUS_ENUM = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];
var createSubtaskContract = defineToolContract({
  name: "create_subtask",
  agent: {
    description: "Create a subtask under the current parent task. Use when breaking a complex parent into smaller pieces during planning. For post-task follow-ups use create_follow_up_task.",
    fields: {
      title: f.string({ desc: "Subtask title" }),
      description: f.optional(f.string({ desc: cardDescriptionDesc("Brief description") })),
      plan: f.optional(f.string({ desc: "Implementation plan in markdown" })),
      ordinal: f.optional(f.number({ desc: "Step/order number (0-based)" })),
      storyPointValue: f.optional(f.number({ desc: SP_DESCRIPTION })),
      followParentStatus: f.optional(f.boolean({ desc: AGENT_FOLLOW_PARENT_STATUS })),
      dependsOn: f.optional(f.array(f.string(), { desc: AGENT_DEPENDS_ON }))
    }
  },
  mcp: {
    description: "Create a subtask under a parent task. Pass projectId to target a specific project; otherwise the configured default project is used. Subtasks break a larger task into independently buildable pieces. For children that instead ship on the parent's own branch/PR (e.g. per-theme tracking cards for one bundled PR), set followParentStatus so their status rides the parent's automatically.",
    fields: {
      projectId: mcpProjectId,
      parentTaskId: f.string({ desc: "The parent task ID" }),
      title: f.string({ desc: "Subtask title" }),
      description: f.optional(f.string({ desc: cardDescriptionDesc("Subtask description") })),
      plan: f.optional(f.string({ desc: "Subtask implementation plan (markdown)" })),
      ordinal: f.optional(f.number({ desc: "Ordering position among siblings" })),
      storyPointValue: f.optional(f.number({ desc: SP_DESCRIPTION })),
      followParentStatus: f.optional(f.boolean({ desc: MCP_FOLLOW_PARENT_STATUS })),
      dependsOn: f.optional(
        f.array(f.string(), {
          desc: "Sibling subtask ids or slugs this subtask blocks on (it won't start until they merge to dev). Set explicit dependency metadata here instead of describing order in the plan text. Omit / leave empty for independent children so they run in parallel."
        })
      ),
      tags: f.optional(
        f.array(f.string(), {
          desc: 'Tag names to assign to the subtask (e.g. ["refactor"]). Unknown names are rejected \u2014 create the tag first with manage_tags. Use list_tags to see available tags.'
        })
      )
    }
  }
});
var updateSubtaskContract = defineToolContract({
  name: "update_subtask",
  agent: {
    description: "Update an existing subtask's fields (title, description, plan, ordinal, storyPointValue, dependsOn) \u2014 and the sanctioned path to make a child buildable: promote it to status Open, assign its agent (agentIdOrName), and set story points. Setting story points does NOT auto-promote; set status explicitly. For the current task use update_task_plan.",
    fields: {
      subtaskId: f.string({ desc: "The subtask ID to update" }),
      title: f.optional(f.string()),
      description: f.optional(f.string({ desc: cardDescriptionDesc("New description") })),
      plan: f.optional(f.string()),
      status: f.optional(
        f.enum(["Planning", "Open"], {
          desc: 'Move the child between "Planning" and "Open". "Open" marks it ready to execute \u2014 required before start_child_cloud_build. Execution statuses transition automatically.'
        })
      ),
      agentIdOrName: f.optional(
        f.string({
          desc: "Assign a project agent to the child (agent id or exact name from the Project Agents list). Required before start_child_cloud_build."
        })
      ),
      ordinal: f.optional(f.number()),
      storyPointValue: f.optional(f.number({ desc: SP_DESCRIPTION })),
      followParentStatus: f.optional(f.boolean({ desc: AGENT_FOLLOW_PARENT_STATUS })),
      dependsOn: f.optional(
        f.array(f.string(), {
          desc: `${AGENT_DEPENDS_ON} Replaces the full dependency set \u2014 pass [] to clear all, omit to leave unchanged.`
        })
      )
    }
  },
  mcp: {
    description: "Update a subtask's fields: title, description, plan, status, ordering, story points, or dependencies. Pass projectId to target a specific project; otherwise the configured default project is used. Moving a subtask beyond Planning auto-fills missing story points and agent assignment \u2014 don't spend turns on them.",
    fields: {
      projectId: mcpProjectId,
      subtaskId: f.string({ desc: "The subtask ID" }),
      title: f.optional(f.string({ desc: "New title" })),
      description: f.optional(f.string({ desc: cardDescriptionDesc("New description") })),
      plan: f.optional(f.string({ desc: "New plan (markdown)" })),
      status: f.optional(f.enum(MCP_STATUS_ENUM, { desc: "New status" })),
      ordinal: f.optional(f.number({ desc: "New ordering position among siblings" })),
      storyPointValue: f.optional(f.number({ desc: SP_DESCRIPTION })),
      followParentStatus: f.optional(f.boolean({ desc: MCP_FOLLOW_PARENT_STATUS })),
      dependsOn: f.optional(
        f.array(f.string(), {
          desc: "Replace the sibling subtask ids/slugs this subtask blocks on (pass [] to clear). Omit to leave dependencies unchanged."
        })
      )
    }
  }
});
var deleteSubtaskContract = defineToolContract({
  name: "delete_subtask",
  agent: {
    description: "Delete a subtask by id. When to use: a subtask was created in error or is no longer needed. Returns: confirmation string.",
    fields: {
      subtaskId: f.string({ desc: "The subtask ID to delete" })
    }
  },
  mcp: {
    description: "Delete a subtask by ID. Pass projectId to target a specific project; otherwise the configured default project is used. This is permanent \u2014 use update_subtask to set status to Cancelled if you only want to close it.",
    fields: {
      projectId: mcpProjectId,
      subtaskId: f.string({ desc: "The subtask ID to delete" })
    }
  }
});
var listSubtasksContract = defineToolContract({
  name: "list_subtasks",
  agent: {
    description: "List all subtasks under the current parent task. Default compact view returns per child: status, agent, story points, PR number/state, dependencies, and holdsBuildSlot \u2014 plus packSlots (in-flight environments vs the PACK_CHILD_LIMIT cap, and which children hold the slots). Use to coordinate child work; pass verbose:true only when you need full description/plan text (large). For non-child tasks use get_task.",
    fields: {
      verbose: f.optional(
        f.boolean({
          desc: "Return full task rows including description and plan text (large \u2014 can exceed tool result limits on big packs). Default: compact orchestration view."
        })
      )
    }
  },
  mcp: {
    description: "List all subtasks of a parent task with their status and ordering. Pass projectId to target a specific project; otherwise the configured default project is used.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The parent task ID" })
    }
  }
});
var subtasksContracts = [
  createSubtaskContract,
  updateSubtaskContract,
  deleteSubtaskContract,
  listSubtasksContract
];

// src/tool-contracts/attachments.ts
var mcpTaskIdOrSlug = f.string({ desc: "The task ID or slug" });
var listTaskFilesContract = defineToolContract({
  name: "list_task_files",
  agent: {
    description: "List all files attached to this task with metadata. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    fields: {}
  },
  mcp: {
    description: "List all files attached to a task with metadata (no contents \u2014 fast and small). Pass projectId to target a specific project; otherwise the configured default project is used. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpTaskIdOrSlug
    }
  }
});
var getAttachmentContract = defineToolContract({
  name: "get_attachment",
  agent: {
    description: "Fetch one task file's content plus metadata by file ID. Call list_task_files first to discover IDs and check sizes \u2014 large binaries may be truncated by the service's size limit.",
    fields: {
      fileId: f.string({ desc: "The file ID to retrieve" })
    }
  },
  mcp: {
    description: "Fetch one task file's content plus metadata by file ID (accepts task id or slug). Pass projectId to target a specific project; otherwise the configured default project is used. Images are returned as viewable image blocks. Large text files (logs, JSON) are returned in pages \u2014 use `offset`/`maxBytes` to read more, or fetch `downloadUrl` for the whole file. Call list_task_files first to discover IDs and sizes.",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpTaskIdOrSlug,
      fileId: f.string({ desc: "The file ID to fetch" }),
      offset: f.optional(
        f.number({
          int: true,
          nonnegative: true,
          desc: "Byte offset into text content (paging). Default 0."
        })
      ),
      maxBytes: f.optional(
        f.number({
          int: true,
          positive: true,
          desc: "Max bytes of text content to return from offset."
        })
      )
    }
  }
});
var attachmentTags = f.optional(
  f.array(f.string(), {
    desc: `Glossary tag names this file is a relevant example of, e.g. a screenshot of the tag page tagged "tag". Use it when the file shows a tagged entity in a particular state: the tag's page lists its recent tagged attachments, so a reader can see what the entity looks like across the app and spot visual changes over time. Names are matched case-insensitively within the project; a name that matches no tag is reported back and never fails the upload. Max 5.`
  })
);
var uploadAttachmentContract = defineToolContract({
  name: "upload_attachment",
  agent: {
    description: "Upload a file (doc, notes, data, diagram, screenshot \u2014 any file type, up to 25MB) as a task attachment AND post it to the task chat in one step \u2014 no follow-up post_to_chat call needed. This is how you deliver a file the user should keep: it attaches to the card. Never publish deliverables as an external Claude artifact. Pass `tags` when the file is a good example of a glossary tag in some state.",
    fields: {
      path: f.string({
        desc: "Path to the file \u2014 absolute, or relative to the workspace root"
      }),
      title: f.optional(
        f.string({ desc: "Short caption posted with the file (defaults to the file name)" })
      ),
      tags: attachmentTags
    }
  },
  mcp: {
    description: "Upload a local file as a task attachment (any file type, up to 25MB). Pass projectId to target a specific project; otherwise the configured default project is used. The file appears under the task's Files. Pass `comment` to also post it to the task chat in the same step, and `tags` when the file is a good example of a glossary tag in some state.",
    fields: {
      projectId: mcpProjectId,
      taskId: mcpTaskIdOrSlug,
      path: f.string({ desc: "Absolute path to the local file to upload" }),
      comment: f.optional(
        f.string({ desc: "When set, also posts the attachment to the task chat with this text" })
      ),
      tags: attachmentTags,
      mimeType: f.optional(
        f.string({ desc: "Override the mime type inferred from the file extension" })
      )
    }
  }
});
var attachmentsContracts = [
  listTaskFilesContract,
  getAttachmentContract,
  uploadAttachmentContract
];

// src/tool-contracts/suggestions.ts
var createSuggestionContract = defineToolContract({
  name: "create_suggestion",
  agent: {
    description: "File a project suggestion (idea/improvement for maintainers to review). Duplicates are AI-deduped into an existing suggestion with an upvote. Returns the suggestion id.",
    fields: {
      title: f.string({ min: 1, desc: "Short title" }),
      description: f.optional(
        f.string({ desc: cardDescriptionDesc("What should change and why") })
      ),
      tag_names: f.optional(f.array(f.string(), { desc: "Tag names to categorize" }))
    }
  },
  mcp: {
    description: "Suggest a feature, improvement, rule, or idea for the project. Pass projectId to target a specific project; otherwise the configured default project is used. Duplicates are deduped and your upvote is recorded.",
    fields: {
      projectId: mcpProjectId,
      title: f.string({ desc: "Suggestion title" }),
      description: f.optional(f.string({ desc: cardDescriptionDesc("Suggestion details") })),
      tagNames: f.optional(
        f.array(f.string(), {
          desc: 'Tag names to categorize the suggestion (e.g., ["agent-runner"])'
        })
      )
    }
  }
});
var suggestionsContracts = [createSuggestionContract];

// src/tool-contracts/pull-request.ts
var createPullRequestContract = defineToolContract({
  name: "create_pull_request",
  agent: {
    description: "Create a GitHub PR for this task. Auto-stages, commits (commitMessage or title default), pushes to origin, then opens the PR. Always use this instead of gh CLI or raw git.",
    fields: {
      title: f.string({ desc: "The PR title" }),
      body: f.string({
        desc: "The PR description/body in markdown. If the diff changes anything a user sees rendered, the body MUST show the visual proof inline, not just point at the card: upload the capture with upload_attachment, then embed the downloadUrl it returns under a '## Screenshots' heading (![caption](url) for images, [caption](url) for video). Attach to the card as well \u2014 the card is the durable home, the PR body is what the reviewer reads."
      }),
      branch: f.optional(
        f.string({
          desc: "The head branch name for the PR. Defaults to the workspace's current checkout, which is also the branch the commits are pushed to. Pass it explicitly if you pushed to a different branch, or if the card may have been renamed (a rename can re-slug the task's stored branch away from the one you are working on)."
        })
      ),
      baseBranch: f.optional(
        f.string({
          desc: "The base branch to target for the PR (e.g. 'main', 'develop'). Defaults to the project's configured dev branch."
        })
      ),
      commitMessage: f.optional(
        f.string({
          desc: "Commit message for staging uncommitted changes. If not provided, a default message based on the PR title will be used."
        })
      ),
      skipVerify: f.optional(
        f.boolean({
          desc: "Controls the local pre-push quality gate (lint/typecheck/test). Defaults to true (--no-verify): the push skips the local gate because you should run gates yourself before opening the PR and CI re-runs them on the resulting PR. Running the full gate synchronously during the push would block the agent's event loop long enough to drop the Conveyor socket connection. Pass false to force the local pre-push hook to run."
        })
      )
    }
  },
  mcp: {
    description: "Open a GitHub pull request for a task's existing branch (the branch must already be pushed to origin). Pass projectId to target a specific project; otherwise the configured default project is used. Moves the task to ReviewPR. Returns the PR number and URL.",
    fields: {
      projectId: mcpProjectId,
      taskId: f.string({ desc: "The task ID whose branch should be opened as a PR" }),
      title: f.string({ desc: "Pull request title" }),
      body: f.string({
        desc: "Pull request body (markdown). For a diff that changes rendered UI, embed the visual proof inline: upload the capture with upload_attachment and paste the downloadUrl it returns as ![caption](url), in addition to leaving it on the card."
      }),
      head: f.optional(
        f.string({ desc: "Source branch for the PR (defaults to the task's branch)" })
      ),
      base: f.optional(
        f.string({ desc: "Target branch for the PR (defaults to the repo default)" })
      )
    }
  }
});
var pullRequestContracts = [createPullRequestContract];

// src/tool-contracts/index.ts
var TOOL_CONTRACTS = Object.fromEntries(
  [
    ...tasksContracts,
    ...tagsContracts,
    ...checklistContracts,
    ...dependenciesContracts,
    ...subtasksContracts,
    ...attachmentsContracts,
    ...suggestionsContracts,
    ...pullRequestContracts
  ].map((contract) => [contract.name, contract])
);
export {
  CARD_DESCRIPTION_FIELD_HINT,
  CARD_DESCRIPTION_LIMIT_MESSAGE,
  CARD_DESCRIPTION_MAX,
  TOOL_CONTRACTS,
  addDependencyContract,
  approveAndMergePrContract,
  approveManualTestContract,
  attachmentsContracts,
  cardDescriptionDesc,
  checklistContracts,
  compileShape,
  createPullRequestContract,
  createSubtaskContract,
  createSuggestionContract,
  defineToolContract,
  deleteSubtaskContract,
  dependenciesContracts,
  editManualTestContract,
  f,
  getAttachmentContract,
  getDependenciesContract,
  getTagContract,
  getTaskContract,
  listManualTestsContract,
  listSubtasksContract,
  listTagsContract,
  listTaskFilesContract,
  mcpProjectId,
  postToChatContract,
  pullRequestContracts,
  queryManualTestsContract,
  readTaskChatContract,
  rejectManualTestContract,
  removeDependencyContract,
  removeManualTestContract,
  searchTasksContract,
  setManualTestsContract,
  storyPointValueDesc,
  subtasksContracts,
  suggestionsContracts,
  tagsContracts,
  tasksContracts,
  updateSubtaskContract,
  uploadAttachmentContract
};
